import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import SheinSyncRangeModal from '@/shein-platform/components/SheinSyncRangeModal';
import BatchQueryModal from '@/shein-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/shein-platform/modules/products/components/batchQueryUtils';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { buildSheinPath } from '@/shein-platform/constants/sheinRoutes';
import { useSheinPageShopScope, stackShopDimensionTabs } from '@/shein-platform/hooks/useSheinPageShopScope';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { extractSheinList, extractSheinTotal, pickField } from '@/shein-platform/utils/sheinResponse';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import FinanceListGoodsCell, { extractGoodsList } from '@/shein-platform/modules/finance/FinanceListGoodsCell';
import SheinDateRangeFilter, {
  toDayEndDateTime,
  toDayStartDateTime,
  validateDateRange,
} from '@/shein-platform/components/SheinDateRangeFilter';
import {
  BILL_TYPE_CHECK,
  BILL_TYPE_REPORT,
  billPrimaryNo,
  CHECK_PAY_PAID,
  CHECK_PAY_TABS,
  CHECK_PAY_UNPAID,
  defaultBillTypeForMode,
  defaultFinancePayTimeType,
  FINANCE_IN_OUT_OPTIONS,
  FINANCE_SECOND_ORDER_TYPE_OPTIONS,
  financeInOutLabel,
  financePaymentMethodLabel,
  financePayTimeField,
  financeSecondOrderTypeLabel,
  financeTabLabel,
  financeTabsForMode,
  FINANCE_API_MODE_FULL,
  FINANCE_API_MODE_UNKNOWN,
  formatLedgerAmount,
  formatMoney,
  formatPayDate,
  resolveFinanceApiMode,
} from '@/shein-platform/modules/finance/financeDisplay';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import { showToast } from '@/utils/toast';
import '@/styles/shein-platform/shein-product-ops.css';

const PRODUCT_ID_OPTIONS = [
  { value: 'skc', label: 'SKC' },
  { value: 'sku', label: 'SKU' },
];

const EMPTY_FILTER = {
  billNo: '',
  incomeExpenditureType: '',
  secondOrderType: '',
  productIdType: 'skc',
  productIdValue: '',
  payTimeStart: '',
  payTimeEnd: '',
};

function rowKey(row, index, billType) {
  const id = row?.id ?? pickField(row, 'id');
  if (id != null && String(id).trim() !== '') {
    return `id:${id}`;
  }
  const primary = billPrimaryNo(row, billType);
  const bz = pickField(row, 'bzOrderNo') || '';
  const second = pickField(row, 'secondOrderType') || '';
  return `${primary}|${bz}|${second}|${index}`;
}

function toIntOrUndefined(raw) {
  if (raw == null || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function buildFinanceListQuery(query, { isReport, checkPayStatus }) {
  const idType = query.productIdType === 'sku' ? 'sku' : 'skc';
  const idValue = !isReport && query.productIdValue ? query.productIdValue : undefined;
  const payTimeType = defaultFinancePayTimeType(isReport, checkPayStatus);
  return {
    source: 'cached',
    billType: isReport ? BILL_TYPE_REPORT : BILL_TYPE_CHECK,
    payStatus: isReport ? undefined : checkPayStatus,
    bzOrderNo: !isReport && query.billNo ? query.billNo : undefined,
    billNo: isReport && query.billNo ? query.billNo : undefined,
    reportOrderNo: isReport && query.billNo ? query.billNo : undefined,
    incomeExpenditureType: !isReport ? toIntOrUndefined(query.incomeExpenditureType) : undefined,
    secondOrderType: !isReport ? toIntOrUndefined(query.secondOrderType) : undefined,
    skuIdType: idValue ? idType : undefined,
    skuKeyword: idValue || undefined,
    payTimeStart: query.payTimeStart ? toDayStartDateTime(query.payTimeStart) : undefined,
    payTimeEnd: query.payTimeEnd ? toDayEndDateTime(query.payTimeEnd) : undefined,
    payTimeField: financePayTimeField(payTimeType),
  };
}

export default function BillListPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const {
    activeShopId: topActiveShopId,
    activeShop,
    loading: shopLoading,
    concreteScopeId,
    allShopsScope,
    shopDimensionTabs,
  } = useSheinPageShopScope();
  const activeShopId = concreteScopeId || (allShopsScope ? '' : topActiveShopId);

  const financeApiMode = useMemo(
    () => resolveFinanceApiMode(activeShop?.shopType),
    [activeShop?.shopType],
  );
  const visibleTabs = useMemo(
    () => financeTabsForMode(financeApiMode),
    [financeApiMode],
  );

  const [billType, setBillType] = useState(BILL_TYPE_CHECK);
  const isReport = billType === BILL_TYPE_REPORT;
  const [checkPayStatus, setCheckPayStatus] = useState(CHECK_PAY_UNPAID);
  const isPaidCheck = !isReport && checkPayStatus === CHECK_PAY_PAID;
  const billNoLabel = isReport ? t('finance.reportNo') : t('finance.bizNo');
  const payTimeLabel = isReport || isPaidCheck ? t('finance.paidDate') : t('finance.estimatePay');

  const [filterDraft, setFilterDraft] = useState({ ...EMPTY_FILTER });
  const [query, setQuery] = useState({ ...EMPTY_FILTER, _nonce: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  /** 各维度数量：未选中的 Tab 也展示 */
  const [dimensionCounts, setDimensionCounts] = useState({
    checkUnpaid: 0,
    checkPaid: 0,
    report: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const selectedRowsRef = useRef(new Map());
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [batchModalField, setBatchModalField] = useState(null);
  const [syncRangeOpen, setSyncRangeOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const pendingSyncRangeRef = useRef(null);

  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();

  const listQueryParams = useMemo(
    () => buildFinanceListQuery(query, { isReport, checkPayStatus }),
    [checkPayStatus, isReport, query],
  );

  const fetchFinanceTotal = useCallback(async (params) => {
    if (!activeShopId) return 0;
    const res = await sheinPlatformAPI.listFinanceBills(activeShopId, {
      page: 1,
      pageSize: 1,
      ...params,
    });
    return extractSheinTotal(res, 0);
  }, [activeShopId]);

  const loadDimensionCounts = useCallback(async () => {
    if (!activeShopId) {
      setDimensionCounts({ checkUnpaid: 0, checkPaid: 0, report: 0 });
      return;
    }
    const needReport = visibleTabs.some((t) => t.id === BILL_TYPE_REPORT);
    const needCheck = visibleTabs.some((t) => t.id === BILL_TYPE_CHECK);
    try {
      const [checkUnpaid, checkPaid, report] = await Promise.all([
        needCheck
          ? fetchFinanceTotal(buildFinanceListQuery(query, {
            isReport: false,
            checkPayStatus: CHECK_PAY_UNPAID,
          }))
          : Promise.resolve(0),
        needCheck
          ? fetchFinanceTotal(buildFinanceListQuery(query, {
            isReport: false,
            checkPayStatus: CHECK_PAY_PAID,
          }))
          : Promise.resolve(0),
        needReport
          ? fetchFinanceTotal(buildFinanceListQuery(query, {
            isReport: true,
            checkPayStatus: CHECK_PAY_UNPAID,
          }))
          : Promise.resolve(0),
      ]);
      setDimensionCounts({
        checkUnpaid: Number(checkUnpaid) || 0,
        checkPaid: Number(checkPaid) || 0,
        report: Number(report) || 0,
      });
    } catch {
      /* 维度数量失败不阻断列表 */
    }
  }, [activeShopId, fetchFinanceTotal, query, visibleTabs]);

  const run = useCallback(async () => {
    if (!activeShopId) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await sheinPlatformAPI.listFinanceBills(activeShopId, {
        page,
        pageSize,
        ...listQueryParams,
      });
      const list = extractSheinList(res);
      const nextTotal = extractSheinTotal(res, list.length);
      setRows(list);
      setTotal(nextTotal);
      setDimensionCounts((prev) => {
        if (isReport) return { ...prev, report: nextTotal };
        if (checkPayStatus === CHECK_PAY_PAID) return { ...prev, checkPaid: nextTotal };
        return { ...prev, checkUnpaid: nextTotal };
      });
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e?.message || t('finance.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeShopId, checkPayStatus, isReport, listQueryParams, page, pageSize, query._nonce, t]);

  const updateFilterField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (shopLoading) return;
    run();
  }, [run, shopLoading]);

  useEffect(() => {
    if (shopLoading) return;
    loadDimensionCounts();
  }, [loadDimensionCounts, shopLoading]);

  useEffect(() => {
    setPage(1);
    setSelectedKeys(new Set());
    setFilterDraft((prev) => ({
      ...prev,
      payTimeStart: '',
      payTimeEnd: '',
    }));
    setQuery((prev) => ({
      ...prev,
      payTimeStart: '',
      payTimeEnd: '',
      _nonce: prev._nonce + 1,
    }));
  }, [billType, checkPayStatus, activeShopId]);

  // 筛选查询变化时清空跨页选中（不含翻页）
  const filterNonce = query._nonce;
  useEffect(() => {
    setSelectedKeys(new Set());
  }, [filterNonce]);

  useEffect(() => {
    const nextDefault = defaultBillTypeForMode(financeApiMode);
    const allowed = visibleTabs.some((t) => t.id === billType);
    if (!allowed) {
      setBillType(nextDefault);
    }
  }, [financeApiMode, visibleTabs, billType]);

  useEffect(() => {
    if (!activeShopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getShopSyncStatus(activeShopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const mod = modules.find((m) => String(m.module || '').toUpperCase() === 'FINANCE');
        const at = mod?.lastSyncEndAt || mod?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [activeShopId, lastSyncedAt]);

  const handleSync = useCallback(async () => {
    const range = pendingSyncRangeRef.current;
    if (!activeShopId || !range?.startTime || !range?.endTime) {
      showToast(t('finance.pickRange'), 'warning');
      return;
    }
    if (financeApiMode === FINANCE_API_MODE_UNKNOWN) {
      showToast(
        t('finance.modeMismatch', { shopType: activeShop?.shopType || t('finance.shopTypeUnset') }),
        'error',
      );
      return;
    }
    try {
      const track = await startJob({
        kind: 'finance',
        shopId: activeShopId,
        label: t('publishRecord.syncUpdate'),
        payload: {
          startAddTime: range.startTime,
          endAddTime: range.endTime,
        },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      const finishedAt = new Date().toISOString();
      setLastSyncedAt(finishedAt);
      setPersistedSyncedAt(finishedAt);
      pendingSyncRangeRef.current = null;
      setPage(1);
      setQuery((prev) => ({ ...prev, _nonce: prev._nonce + 1 }));
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      showToast(e?.message || t('productAction.syncFailed'), 'error');
    }
  }, [activeShop?.shopType, activeShopId, financeApiMode, startJob, onTrackDone, t]);

  const {
    refresh: guardedSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncingLocal || isBusy('finance', activeShopId);

  const productIdLabel = PRODUCT_ID_OPTIONS.find((o) => o.value === filterDraft.productIdType)?.label || 'SKC';

  const onQuery = () => {
    const tokens = parseBatchTokens(filterDraft.billNo).slice(0, BATCH_QUERY_LIMIT);
    const productTokens = parseBatchTokens(filterDraft.productIdValue).slice(0, BATCH_QUERY_LIMIT);
    const rangeError = validateDateRange(filterDraft.payTimeStart, filterDraft.payTimeEnd);
    if (rangeError) {
      showToast(rangeError, 'warning');
      return;
    }
    if (activeShopId) setLoading(true);
    setPage(1);
    setQuery((prev) => ({
      ...EMPTY_FILTER,
      billNo: tokens.join(','),
      incomeExpenditureType: filterDraft.incomeExpenditureType || '',
      secondOrderType: filterDraft.secondOrderType || '',
      productIdType: filterDraft.productIdType === 'sku' ? 'sku' : 'skc',
      productIdValue: productTokens.join(','),
      payTimeStart: filterDraft.payTimeStart || '',
      payTimeEnd: filterDraft.payTimeEnd || '',
      _nonce: prev._nonce + 1,
    }));
  };

  const onReset = () => {
    setFilterDraft({ ...EMPTY_FILTER });
    setPage(1);
    setQuery((prev) => ({
      ...EMPTY_FILTER,
      _nonce: prev._nonce + 1,
    }));
  };

  const openDetail = (row) => {
    const no = billPrimaryNo(row, billType);
    if (!no) return;
    navigate(buildSheinPath(`finance/${billType}/${encodeURIComponent(no)}`));
  };

  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : t('tiktokCatalog.notSynced');

  const emptyTitle = !activeShopId
    ? (allShopsScope ? t('draftBox.pickConcrete') : t('returnList.pickShop'))
    : loading
      ? t('returnList.loading')
      : (isReport
        ? t('finance.emptyReport')
        : (isPaidCheck ? t('finance.emptyPaid') : t('finance.emptyUnpaid')));

  const emptyDesc = !activeShopId || loading
    ? null
    : t('finance.emptyPull', { platform: 'SHEIN' });

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, index, billType)),
    [rows, billType],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedRows = useMemo(() => {
    const map = selectedRowsRef.current;
    for (const key of [...map.keys()]) {
      if (!selectedKeys.has(key)) map.delete(key);
    }
    rows.forEach((row, index) => {
      const key = rowKey(row, index, billType);
      if (selectedKeys.has(key)) map.set(key, row);
    });
    return Array.from(selectedKeys)
      .map((key) => map.get(key))
      .filter(Boolean);
  }, [rows, selectedKeys, billType]);


  const buildExportRows = (list) => {
    if (isReport) {
      const headers = [
        'reportOrderNo',
        'rxAcct',
        'completedPayTime',
        'paymentMethod',
        'amount',
        'currencyCode',
      ];
      const csvRows = (Array.isArray(list) ? list : []).map((row) => ({
        reportOrderNo: billPrimaryNo(row, BILL_TYPE_REPORT),
        rxAcct: pickField(row, 'rxAcct') || '',
        completedPayTime: pickField(row, 'completedPayTime') || '',
        paymentMethod: financePaymentMethodLabel(pickField(row, 'paymentMethod')),
        amount: pickField(row, 'amount', 'income', 'estimateIncomeMoneyTotal'),
        currencyCode: pickField(row, 'currencyCode'),
      }));
      return { headers, csvRows };
    }
    const headers = [
      'platformOrderNo',
      'goodsTitles',
      'secondOrderType',
      'incomeExpenditureType',
      isPaidCheck ? 'completedPayTime' : 'estimatePayTime',
      'amount',
      'currencyCode',
    ];
    const csvRows = (Array.isArray(list) ? list : []).map((row) => {
      const goods = extractGoodsList(row);
      return {
        platformOrderNo: pickField(row, 'bzOrderNo') || '',
        goodsTitles: goods
          .map((g) => {
            const title = pickField(g, 'goodsTitle', 'productName', 'title') || '';
            const merchantSku = pickField(g, 'supplierSku', 'sellerSku', 'merchantSku') || '';
            if (title && merchantSku) return `${title}(${merchantSku})`;
            return title || merchantSku || '';
          })
          .filter(Boolean)
          .join(' | '),
        secondOrderType: financeSecondOrderTypeLabel(pickField(row, 'secondOrderType')),
        incomeExpenditureType: financeInOutLabel(pickField(row, 'incomeExpenditureType')),
        ...(isPaidCheck
          ? { completedPayTime: pickField(row, 'completedPayTime') || '' }
          : { estimatePayTime: pickField(row, 'estimatePayTime') || '' }),
        amount: pickField(row, 'amount', 'estimateIncomeMoneyTotal'),
        currencyCode: pickField(row, 'currencyCode'),
      };
    });
    return { headers, csvRows };
  };

  const exportRows = (list, emptyMessage) => {
    if (!list.length) {
      showToast(emptyMessage, 'warning');
      return;
    }
    const { headers, csvRows } = buildExportRows(list);
    const prefix = isReport ? 'shein-finance-settlements' : 'shein-finance-ledgers';
    downloadCsv(`${prefix}-${Date.now()}.csv`, headers, csvRows);
    showToast(t('tiktokCatalog.exported', { count: csvRows.length }), 'success');
  };

  const exportSelected = () => {
    exportRows(selectedRows, t('finance.pickExport', { name: financeTabLabel(billType) }));
  };

  const exportPage = () => {
    exportRows(rows, t('finance.noneExport', { name: financeTabLabel(billType) }));
  };

  const exportFiltered = async () => {
    if (!activeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (total <= 0 && rows.length === 0) {
      showToast(t('finance.noneExport', { name: financeTabLabel(billType) }), 'warning');
      return;
    }
    setExporting(true);
    try {
      const all = await fetchAllPages(async (pageNo, size) => {
        const res = await sheinPlatformAPI.listFinanceBills(activeShopId, {
          page: pageNo,
          pageSize: size,
          ...listQueryParams,
        });
        const list = extractSheinList(res);
        return { items: list, total: extractSheinTotal(res, list.length) };
      });
      exportRows(all, t('finance.noneExport', { name: financeTabLabel(billType) }));
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const typeTabs = (
    <div className="spo-finance-tabs">
      <div className="spo-status-tabs" role="tablist" aria-label={t('finance.billType')}>
        {visibleTabs.map((tab) => {
          const active = billType === tab.id;
          const count = tab.id === BILL_TYPE_REPORT
            ? (Number(dimensionCounts.report) || 0)
            : ((Number(dimensionCounts.checkUnpaid) || 0) + (Number(dimensionCounts.checkPaid) || 0));
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`spo-status-tab${active ? ' spo-status-tab--active' : ''}`}
              onClick={() => setBillType(tab.id)}
            >
              {t(tab.labelKey)}
              <span className="spo-status-tab__count">{count}</span>
            </button>
          );
        })}
      </div>
      {!isReport ? (
        <div className="spo-status-tabs spo-finance-pay-tabs" role="tablist" aria-label={t('finance.payAria')}>
          {CHECK_PAY_TABS.map((tab) => {
            const active = checkPayStatus === tab.id;
            const count = tab.id === CHECK_PAY_PAID
              ? (Number(dimensionCounts.checkPaid) || 0)
              : (Number(dimensionCounts.checkUnpaid) || 0);
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`spo-status-tab spo-status-tab--sub${active ? ' spo-status-tab--active' : ''}`}
                onClick={() => setCheckPayStatus(tab.id)}
              >
                {t(tab.labelKey)}
                <span className="spo-status-tab__count">{count}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );

  const colSpan = 7;

  const onBatchDelete = async () => {
    if (!activeShopId || selectedKeys.size === 0) {
      showToast(t('finance.pickDelete'), 'warning');
      return;
    }
    const ids = selectedRows
      .map((row) => Number(row?.id ?? pickField(row, 'id')))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length === 0) {
      showToast(t('finance.missingId', { platform: 'SHEIN' }), 'error');
      return;
    }
    const ok = await showSheinConfirm({
      title: t('returnList.batchDelete'),
      message: t('finance.deleteMsg', { count: ids.length, platform: 'SHEIN' }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await sheinPlatformAPI.batchDeleteFinanceBills(activeShopId, ids);
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : ids.length;
      if (count > 0) {
        showToast(t('finance.deletedCache', { count }), 'success');
      } else {
        showToast(t('certList.noneDeletedCache'), 'warning');
      }
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await run();
      await loadDimensionCounts();
    } catch (e) {
      showToast(e?.message || t('shop.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <SheinSyncProgressSlot kinds={['finance']} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || exporting || deleting}
            syncKinds={['finance']}
            onSync={() => {
              if (!activeShopId) {
                showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
                return;
              }
              setSyncRangeOpen(true);
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={shopLoading || !activeShopId || syncing}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('finance.syncHint', { platform: 'SHEIN' })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={exporting || !activeShopId}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!activeShopId || deleting}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            {isReport ? (
              <div className="spo-field spo-field--inline spo-field--batch spo-field--w300">
                <span className="spo-field__label">{billNoLabel}</span>
                <input
                  className="spo-input"
                  value={filterDraft.billNo}
                  placeholder={t('tiktokCatalog.idPlaceholder', { label: billNoLabel })}
                  disabled={!activeShopId}
                  onChange={(e) => updateFilterField('billNo', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                />
                <button
                  type="button"
                  className="spo-batch-add-btn"
                  aria-label={t('tiktokCatalog.batchInput', { label: billNoLabel })}
                  title={t('tiktokCatalog.batchQuery')}
                  disabled={!activeShopId}
                  onClick={() => setBatchModalField('billNo')}
                >
                  <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
                </button>
              </div>
            ) : (
              <>
                <div className="spo-field spo-field--inline spo-field--status">
                  <span className="spo-field__label">{t('finance.inOut')}</span>
                  <select
                    className="spo-select spo-select--status"
                    value={filterDraft.incomeExpenditureType}
                    disabled={!activeShopId}
                    aria-label={t('finance.inOut')}
                    onChange={(e) => updateFilterField('incomeExpenditureType', e.target.value)}
                  >
                    <option value="">{t('tiktokCatalog.all')}</option>
                    {FINANCE_IN_OUT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{financeInOutLabel(opt.value)}</option>
                    ))}
                  </select>
                </div>
                <div className="spo-field spo-field--inline spo-field--w220">
                  <span className="spo-field__label">{t('finance.billType')}</span>
                  <select
                    className="spo-select"
                    value={filterDraft.secondOrderType}
                    disabled={!activeShopId}
                    aria-label={t('finance.billType')}
                    onChange={(e) => updateFilterField('secondOrderType', e.target.value)}
                  >
                    <option value="">{t('certLib.allTypes')}</option>
                    {FINANCE_SECOND_ORDER_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{financeSecondOrderTypeLabel(opt.value)}</option>
                    ))}
                  </select>
                </div>
                <div className="spo-field spo-field--inline spo-field--id-combo spo-field--batch spo-field--w300">
                  <select
                    className="spo-select spo-select--compact"
                    value={filterDraft.productIdType}
                    disabled={!activeShopId}
                    aria-label={t('finance.idType')}
                    onChange={(e) => updateFilterField('productIdType', e.target.value)}
                  >
                    {PRODUCT_ID_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <input
                    className="spo-input"
                    value={filterDraft.productIdValue}
                    placeholder={t('tiktokCatalog.idPlaceholder', { label: productIdLabel })}
                    disabled={!activeShopId}
                    onChange={(e) => updateFilterField('productIdValue', e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                  />
                  <button
                    type="button"
                    className="spo-batch-add-btn"
                    aria-label={t('tiktokCatalog.batchInput', { label: productIdLabel })}
                    title={t('tiktokCatalog.batchQuery')}
                    disabled={!activeShopId}
                    onClick={() => setBatchModalField('productIdValue')}
                  >
                    <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
                  </button>
                </div>
                <div className="spo-field spo-field--inline spo-field--batch spo-field--w300">
                  <span className="spo-field__label">{billNoLabel}</span>
                  <input
                    className="spo-input"
                    value={filterDraft.billNo}
                    placeholder={t('tiktokCatalog.idPlaceholder', { label: billNoLabel })}
                    disabled={!activeShopId}
                    onChange={(e) => updateFilterField('billNo', e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                  />
                  <button
                    type="button"
                    className="spo-batch-add-btn"
                    aria-label={t('tiktokCatalog.batchInput', { label: billNoLabel })}
                    title={t('tiktokCatalog.batchQuery')}
                    disabled={!activeShopId}
                    onClick={() => setBatchModalField('billNo')}
                  >
                    <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
                  </button>
                </div>
              </>
            )}
            <SheinDateRangeFilter
              showTimeType={false}
              timeTypeLabel={payTimeLabel}
              startDate={filterDraft.payTimeStart}
              endDate={filterDraft.payTimeEnd}
              disabled={!activeShopId}
              timeTypeAriaLabel={payTimeLabel}
              onStartDateChange={(v) => updateFilterField('payTimeStart', v)}
              onEndDateChange={(v) => updateFilterField('payTimeEnd', v)}
            />
          </div>

          <div className="spo-filter-bar__actions">
            <button
              type="button"
              className="spo-btn spo-btn--secondary spo-btn--sm"
              disabled={!activeShopId}
              onClick={onReset}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="spo-btn spo-btn--primary spo-btn--sm"
              disabled={!activeShopId || loading}
              onClick={onQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
        {error ? (
          <div className="spo-ops-error" role="alert">{error}</div>
        ) : null}
      </ProductFilterCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={t('finance.batchQueryNamed', { label: batchModalField === 'productIdValue' ? productIdLabel : billNoLabel })}
        initialValue={
          batchModalField === 'productIdValue'
            ? filterDraft.productIdValue
            : filterDraft.billNo
        }
        placeholder={t('orderList.idLinePh', { type: batchModalField === 'productIdValue' ? productIdLabel : billNoLabel })}
        onConfirm={(tokens) => {
          const joined = joinBatchTokens(tokens);
          if (batchModalField === 'productIdValue') {
            setFilterDraft((prev) => ({ ...prev, productIdValue: joined }));
          } else {
            setFilterDraft((prev) => ({ ...prev, billNo: joined }));
          }
        }}
        onClose={() => setBatchModalField(null)}
      />

      <SheinSyncRangeModal
        open={syncRangeOpen}
        title={t('finance.syncTitle')}
        defaultHours={24 * 7}
        presets={[
          { label: t('orderList.last24h'), hours: 24 },
          { label: t('orderList.last7d'), hours: 24 * 7 },
          { label: t('orderList.last14d'), hours: 24 * 14 },
          { label: t('orderList.last30d'), hours: 24 * 30 },
          { label: t('orderList.last90d'), hours: 24 * 90 },
        ]}
        confirming={syncing}
        onConfirm={async (range) => {
          pendingSyncRangeRef.current = range;
          const ok = await guardedSync();
          if (ok !== false) setSyncRangeOpen(false);
        }}
        onClose={() => {
          if (!syncing) setSyncRangeOpen(false);
        }}
      />

      <ProductListCard tabs={stackShopDimensionTabs(shopDimensionTabs, typeTabs)} loading={Boolean(activeShopId) && loading} loadingLabel={t('finance.querying')}>
        <div className="spo-drafts__table-wrap spo-ops-table-wrap spo-finance-list__table-wrap">
          <table className={`spo-publish__table spo-drafts__table spo-ops-table spo-finance-list__table${isReport ? ' spo-finance-list__table--report' : ''}`}>
            <colgroup>
              <col className="spo-ops-col spo-ops-col--check shein-product-col--check" />
              {isReport ? (
                <>
                  <col />
                  <col />
                  <col />
                  <col />
                  <col className="spo-ops-col spo-ops-col--absorb" />
                  <col className="spo-ops-col spo-ops-col--ops" />
                </>
              ) : (
                <>
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col className="spo-ops-col spo-ops-col--absorb" />
                </>
              )}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="spo-ops-th spo-ops-th--check shein-product-cell--spu-check"
                  onClick={() => {
                    if (!activeShopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('finance.selectAll', { name: financeTabLabel(billType) })}
                    disabled={!activeShopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                {isReport ? (
                  <>
                    <th scope="col">{t('finance.reportNo')}</th>
                    <th scope="col">{t('finance.account')}</th>
                    <th scope="col">{t('finance.paidDate')}</th>
                    <th scope="col">{t('finance.payMethod')}</th>
                    <th scope="col">{t('finance.settledAmt')}</th>
                    <th scope="col" className="spo-ops-th--ops">{t('certLib.colOps')}</th>
                  </>
                ) : (
                  <>
                    <th scope="col">{t('orderList.productInfo')}</th>
                    <th scope="col">{t('finance.bizNo')}</th>
                    <th scope="col">{t('finance.billType')}</th>
                    <th scope="col">{t('finance.inOut')}</th>
                    <th scope="col">{payTimeLabel}</th>
                    <th scope="col">{t('finance.receivable')}</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {!activeShopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="spo-certs-empty-cell">
                    <div className="spo-certs-empty">
                      <p className="spo-certs-empty__title">{emptyTitle}</p>
                      {emptyDesc ? <p className="spo-certs-empty__desc">{emptyDesc}</p> : null}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const key = rowKey(row, index, billType);
                  const primaryNo = billPrimaryNo(row, billType);
                  if (isReport) {
                    const settled = formatMoney(
                      pickField(row, 'amount', 'income', 'estimateIncomeMoneyTotal'),
                      pickField(row, 'currencyCode'),
                    );
                    return (
                      <tr key={key}>
                        <td
                          className="spo-ops-td spo-ops-td--check shein-product-cell--spu-check"
                          onClick={() => toggleOne(key)}
                        >
                          <input
                            type="checkbox"
                            aria-label={t('orderList.selectRow', { title: primaryNo || key })}
                            checked={selectedKeys.has(key)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleOne(key)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="spo-finance-link"
                            disabled={!primaryNo}
                            onClick={() => openDetail(row)}
                          >
                            {primaryNo || '—'}
                          </button>
                        </td>
                        <td>{pickField(row, 'rxAcct') || '—'}</td>
                        <td>{formatPayDate(pickField(row, 'completedPayTime'))}</td>
                        <td>{financePaymentMethodLabel(pickField(row, 'paymentMethod'))}</td>
                        <td className="spo-finance-amt">{settled}</td>
                        <td className="spo-certs-td spo-certs-td--ops spo-ops-td--ops">
                          <div className="spo-drafts__actions">
                            <button
                              type="button"
                              className="spo-btn spo-btn--primary spo-btn--sm"
                              disabled={!primaryNo}
                              onClick={() => openDetail(row)}
                            >
                              {t('finance.viewDetail')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  const ledgerAmt = formatLedgerAmount(
                    pickField(row, 'amount', 'estimateIncomeMoneyTotal'),
                    pickField(row, 'currencyCode'),
                    pickField(row, 'incomeExpenditureType'),
                  );
                  const inOut = financeInOutLabel(pickField(row, 'incomeExpenditureType'));
                  const goods = extractGoodsList(row);
                  const bzOrderNo = pickField(row, 'bzOrderNo') || '';
                  return (
                    <tr key={key}>
                      <td
                        className="spo-ops-td spo-ops-td--check shein-product-cell--spu-check"
                        onClick={() => toggleOne(key)}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('orderList.selectRow', { title: primaryNo || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key)}
                        />
                      </td>
                      <td className="spo-ops-td spo-ops-td--product">
                        <FinanceListGoodsCell goods={goods} />
                      </td>
                      <td className="spo-ops-td spo-ops-td--orderno">
                        <div className="spo-finance-bz-cell">
                          <HoverCopyField
                            className="spo-return-copy"
                            value={bzOrderNo}
                            display={bzOrderNo ? <strong>{bzOrderNo}</strong> : '—'}
                            emptyText="—"
                          />
                          {goods.length > 0 ? (
                            <div className="spo-finance-bz-cell__count">{t('finance.orderCount', { count: goods.length })}</div>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <span className="spo-finance-bill-type">
                          {financeSecondOrderTypeLabel(pickField(row, 'secondOrderType'))}
                        </span>
                      </td>
                      <td>
                        <span className={`spo-finance-inout spo-finance-inout--${Number(pickField(row, 'incomeExpenditureType')) === 2 ? 'out' : 'in'}`}>
                          {inOut}
                        </span>
                      </td>
                      <td>
                        {formatPayDate(
                          isPaidCheck
                            ? pickField(row, 'completedPayTime')
                            : pickField(row, 'estimatePayTime'),
                        )}
                      </td>
                      <td className={`spo-finance-amt spo-finance-amt--${ledgerAmt.tone}`}>
                        {ledgerAmt.text}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <SheinOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!activeShopId}
          pageItemCount={rows.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('finance.pager')}
        />
      </ProductListCard>
    </>
  );
}
